import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiFetch } from '../lib/api.js';
import { getActiveOrgId, setActiveOrgId } from '../lib/activeOrg.js';
import type { Me, OrgSummary } from '../lib/types.js';

interface OrgState {
  me: Me | null;
  orgs: OrgSummary[];
  orgId: string | null;
  activeOrg: OrgSummary | null;
  loading: boolean;
  error: string | null;
  setOrg: (id: string) => void;
  reload: () => Promise<void>;
}

const OrgContext = createContext<OrgState | undefined>(undefined);

export function OrgProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [orgId, setOrgId] = useState<string | null>(getActiveOrgId());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    try {
      const data = await apiFetch<Me>('/api/me');
      setMe(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load account');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const orgs = (me?.memberships ?? []).map((m) => m.org).filter((o): o is OrgSummary => o !== null);

  // Keep a valid active org selected.
  const activeOrg = orgs.find((o) => o.id === orgId) ?? orgs[0] ?? null;
  useEffect(() => {
    if (activeOrg && activeOrg.id !== orgId) {
      setOrgId(activeOrg.id);
      setActiveOrgId(activeOrg.id);
    }
  }, [activeOrg, orgId]);

  function setOrg(id: string) {
    setOrgId(id);
    setActiveOrgId(id);
  }

  return (
    <OrgContext.Provider
      value={{ me, orgs, orgId: activeOrg?.id ?? null, activeOrg, loading, error, setOrg, reload }}
    >
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within an OrgProvider');
  return ctx;
}
